const YOUTUBE_HOSTS = new Set(['www.youtube.com', 'youtube.com', 'm.youtube.com', 'youtu.be']);

export function isAllowedYouTubeEmbedUrl(rawUrl: string): boolean {
    let url: URL;
    try {
        url = new URL(rawUrl);
    }
    catch {
        return false;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return false;
    }

    const host = url.hostname.toLowerCase();
    if (!YOUTUBE_HOSTS.has(host)) {
        return false;
    }

    if (host === 'youtu.be') {
        return url.pathname.length > 1;
    }

    if (url.pathname === '/watch') {
        return url.searchParams.has('v');
    }

    return url.pathname.startsWith('/embed/');
}

const CLOUDFLARE_STREAM_UID = /^[a-zA-Z0-9_-]{1,128}$/;

export function isCloudflareStreamUid(uid: string): boolean {
    return CLOUDFLARE_STREAM_UID.test(uid);
}

const INSTAGRAM_HOSTS = new Set(['www.instagram.com', 'instagram.com']);
/** Post / reel shortcodes are typically 5–64 URL-safe characters. */
const INSTAGRAM_SHORTCODE = /^[A-Za-z0-9_-]{5,64}$/;
const INSTAGRAM_PATH = /^\/(p|reel|reels)\/([A-Za-z0-9_-]{5,64})\/?$/i;

export type ParsedInstagramPost = {
    shortcode: string;
    kind: 'p' | 'reel';
    /** Official embed iframe URL (no third-party script). */
    embedUrl: string;
};

export function parseInstagramPostUrl(rawUrl: string): ParsedInstagramPost | null {
    let url: URL;
    try {
        url = new URL(rawUrl);
    }
    catch {
        return null;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return null;
    }

    const host = url.hostname.toLowerCase();
    if (!INSTAGRAM_HOSTS.has(host)) {
        return null;
    }

    const match = INSTAGRAM_PATH.exec(url.pathname);
    if (!match) {
        return null;
    }

    const kindRaw = match[1].toLowerCase();
    const shortcode = match[2];
    if (!INSTAGRAM_SHORTCODE.test(shortcode)) {
        return null;
    }

    const kind: 'p' | 'reel' = kindRaw === 'p' ? 'p' : 'reel';
    const pathKind = kind === 'p' ? 'p' : 'reel';

    return {
        shortcode,
        kind,
        embedUrl: `https://www.instagram.com/${pathKind}/${shortcode}/embed/`,
    };
}

export function isAllowedInstagramPostUrl(rawUrl: string): boolean {
    return parseInstagramPostUrl(rawUrl) != null;
}

const X_HOSTS = new Set([
    'x.com',
    'www.x.com',
    'twitter.com',
    'www.twitter.com',
    'mobile.twitter.com',
    'mobile.x.com',
]);
const X_STATUS_PATH = /^\/[^/]+\/status\/(\d{5,25})\/?$/i;

/** Numeric status id from an X / Twitter status URL. */
export function parseXPostStatusId(rawUrl: string): string | null {
    let url: URL;
    try {
        url = new URL(rawUrl);
    }
    catch {
        return null;
    }

    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        return null;
    }

    const host = url.hostname.toLowerCase();
    if (!X_HOSTS.has(host)) {
        return null;
    }

    const match = X_STATUS_PATH.exec(url.pathname);
    return match?.[1] ?? null;
}

export function isAllowedXPostUrl(rawUrl: string): boolean {
    return parseXPostStatusId(rawUrl) != null;
}
